/**
 * Light or dark, decided the same way language is.
 *
 * ---------------------------------------------------------------------------
 * THREE STATES, NOT TWO
 * ---------------------------------------------------------------------------
 *
 * `system` is a real value and the default. It is not "light" — it means *this
 * page has no opinion*, and the stylesheet answers with `prefers-color-scheme`.
 * Collapsing it into a boolean is what produces an app that is stuck in light
 * mode on a device that has been dark since sunset.
 *
 * The CSS is already written for it: bare `:root` carries the light palette,
 * `@media (prefers-color-scheme: dark)` guarded by `:root:not([data-theme=light])`
 * carries dark for the automatic case, and `:root[data-theme=dark]` lets an
 * explicit choice win in both directions. So the only job here is to stamp the
 * attribute — or to remove it, which is what returning to `system` has to do.
 *
 * ---------------------------------------------------------------------------
 * WHY AN EMBEDDED WIDGET IS DIFFERENT
 * ---------------------------------------------------------------------------
 *
 * Following the device is right for a page we own and wrong inside somebody
 * else's. A dark widget on a park's light page is not dark mode, it is a hole. So
 * a host states the scheme and the widget obeys; only an app that owns its page
 * asks the device.
 */

export type Scheme = 'system' | 'light' | 'dark';

const SCHEMES: readonly Scheme[] = ['system', 'light', 'dark'];

export function isScheme(v: unknown): v is Scheme {
  return typeof v === 'string' && (SCHEMES as readonly string[]).includes(v);
}

/**
 * Put the choice on an element, or take it off.
 *
 * `system` REMOVES the attribute rather than setting it to a value, because the
 * stylesheet's automatic branch is keyed on the attribute's absence. Writing
 * `data-theme="system"` would leave a page that follows nothing.
 */
export function applyScheme(scheme: Scheme, root?: HTMLElement): void {
  const el = root ?? (typeof document === 'undefined' ? null : document.documentElement);
  if (!el) return;
  if (scheme === 'system') el.removeAttribute('data-theme');
  else el.setAttribute('data-theme', scheme);
}

/** What the device asks for, when nothing else has an opinion. */
export function deviceScheme(): 'light' | 'dark' {
  if (typeof window === 'undefined' || !window.matchMedia) return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

export function readScheme(key: string): Scheme {
  try {
    const v = localStorage.getItem(key);
    return isScheme(v) ? v : 'system';
  } catch {
    // Private mode. Following the device is the right answer, not a failure.
    return 'system';
  }
}

export function writeScheme(key: string, scheme: Scheme): void {
  try { localStorage.setItem(key, scheme); } catch { /* private mode */ }
}
